/**
 * Hero email-capture form for the landing waitlist / signup prefill.
 *
 * On submit:
 *   1. Fire-and-forget POST to /api/waitlist (abandonment net — failures
 *      are silently ignored so they never block the main flow).
 *   2. Navigate to /signup?email=<urlencoded> so the signup form
 *      arrives prefilled and the visitor only needs to set a password.
 *
 * Email validation is permissive — the backend is source of truth.
 * The frontend only catches the empty-string case to keep the button
 * cleanly disabled without flashing a server-side rejection.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../lib/constants';

interface Props {
  fineprint?: string;
}

type Status = 'idle' | 'sending';

const WaitlistForm: React.FC<Props> = ({
  fineprint = 'Get your edge in the game - 7-day free trial',
}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || status === 'sending') return;
    setStatus('sending');

    // Fire-and-forget: add to waitlist as abandonment net.
    // Failures are intentionally swallowed — the user journey continues
    // to /signup regardless of whether this POST succeeds.
    fetch(`${API_BASE_URL}/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: trimmed, referer: window.location.href }),
    }).catch(() => { /* silently ignore */ });

    // Navigate to signup with the email prefilled.
    navigate(`/signup?email=${encodeURIComponent(trimmed)}`);
  };

  return (
    <form className="gv-waitlist" onSubmit={submit} aria-label="Create a free account">
      <div className="gv-waitlist-row">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="trader@desk.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          aria-describedby="gv-waitlist-fp"
          required
        />
        <button
          type="submit"
          className="gv-waitlist-btn"
          disabled={status === 'sending' || !email.trim()}
        >
          {status === 'sending' ? 'LOADING…' : 'Create free account →'}
        </button>
      </div>
      <div id="gv-waitlist-fp" className="gv-waitlist-fp">{fineprint}</div>
    </form>
  );
};

export default WaitlistForm;
